import * as React from "react";
import { CentralizedDrawer } from "@west-tennessee-consulting/ui";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { clearRecents } from "./recents";
import type { SelectOption } from "./types";

interface ResponsiveCommandSelectMobileProps {
  drawerId: string;
  title: string;
  description?: string;
  trigger: React.ReactNode;
  hasRecents: boolean;
  recentOptions: SelectOption[];
  recentValueSet: Set<string>;
  options: SelectOption[];
  value: string;
  effectiveSearch: string;
  onSelect: (id: string) => void;
  enableRecents: boolean;
  recentKey?: string;
  onClearRecents: () => void;
  customOptionRenderer?: (option: SelectOption) => React.ReactNode;
}

export function ResponsiveCommandSelectMobile({
  drawerId,
  title,
  description,
  trigger,
  hasRecents,
  recentOptions,
  recentValueSet,
  options,
  value,
  effectiveSearch,
  onSelect,
  enableRecents,
  recentKey,
  onClearRecents,
  customOptionRenderer,
}: ResponsiveCommandSelectMobileProps) {
  return (
    <>
      {trigger}
      <CentralizedDrawer
        drawerId={drawerId}
        side="bottom"
        title={title}
        description={description}
        showCloseButton={true}
        enableSwipeGesture={true}
        zIndex={10000}
      >
        <div className="flex-1 min-h-0 overflow-y-auto py-2">
          <Command className="h-full">
            <CommandList className="max-h-none" style={{ maxHeight: "none" }}>
              {hasRecents && (
                <CommandGroup heading="Recent">
                  {recentOptions.map((opt) => (
                    <CommandItem
                      key={`recent-${opt.value}`}
                      onSelect={() => !opt.disabled && onSelect(opt.value)}
                      disabled={opt.disabled}
                      className={cn(
                        "w-full justify-start min-h-[56px] px-6 py-4 rounded-none border-b border-gray-100 last:border-b-0 transition-colors duration-150",
                        opt.value === value &&
                          "bg-primary/10 text-primary border-l-2 border-l-primary",
                        opt.disabled && "opacity-50 cursor-not-allowed",
                        "hover:bg-muted active:bg-muted/80"
                      )}
                    >
                      <div className="flex items-center w-full">
                        <div className="flex-1 text-left">
                          {customOptionRenderer ? (
                            customOptionRenderer(opt)
                          ) : (
                            <>
                              <div className="text-base">{opt.label}</div>
                              {opt.description && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {opt.description}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {opt.value === value && (
                          <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandGroup heading={hasRecents ? "All Options" : undefined}>
                {options
                  .filter((opt) => !recentValueSet.has(opt.value))
                  .filter((opt) => {
                    const s = (effectiveSearch || "").toLowerCase();
                    if (!s) return true;
                    const hay = `${opt.label} ${opt.searchableText || ""}`.toLowerCase();
                    return hay.includes(s);
                  })
                  .map((opt) => (
                    <CommandItem
                      key={opt.value}
                      onSelect={() => !opt.disabled && onSelect(opt.value)}
                      disabled={opt.disabled}
                      className={cn(
                        "w-full justify-start min-h-[56px] px-6 py-4 rounded-none border-b border-gray-100 last:border-b-0 transition-colors duration-150",
                        opt.value === value &&
                          "bg-primary/10 text-primary border-l-2 border-l-primary",
                        opt.disabled && "opacity-50 cursor-not-allowed",
                        "hover:bg-muted active:bg-muted/80"
                      )}
                    >
                      <div className="flex items-center w-full">
                        <div className="flex-1 text-left">
                          {customOptionRenderer ? (
                            customOptionRenderer(opt)
                          ) : (
                            <>
                              <div className="text-base">{opt.label}</div>
                              {opt.description && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {opt.description}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {opt.value === value && (
                          <Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
                        )}
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
        <div className="flex items-center justify-end p-2">
          {enableRecents && recentKey && (
            <Button
              type="button"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                try {
                  clearRecents(recentKey);
                } catch {}
                onClearRecents();
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear recents
            </Button>
          )}
        </div>
      </CentralizedDrawer>
    </>
  );
}
